import React, { useMemo } from 'react';
import { AlertTriangle, Info, TrendingDown } from 'lucide-react';
import ChampionCoachTags from './ChampionCoachTags';
import { analyzeChampionPool } from '../lib/championArchetypes';
import { MONTH_MS } from '../constants';

/**
 * APEX v8.8 — ChampionCoachingView
 *
 * Sous-onglet COACHING de l'onglet Champions.
 *   - Analyse du pool (alertes : trop large, hors pool, WR critique)
 *   - Tags de coaching par champion joué (façon Porofessor, style néon)
 */

const ALERT_STYLES = {
  warning: { border: 'border-amber-400/40', bg: 'bg-amber-400/5',  icon: AlertTriangle, color: 'text-amber-400' },
  info:    { border: 'border-cyan-400/40',  bg: 'bg-cyan-400/5',   icon: Info,          color: 'text-cyan-400' },
  danger:  { border: 'border-rose-400/40',  bg: 'bg-rose-400/5',   icon: TrendingDown,  color: 'text-rose-400' },
};

export default function ChampionCoachingView({ games, userSettings }) {
  const role = userSettings?.primary_role || 'ADC';
  const pool = useMemo(() => {
    const p = userSettings?.champion_pool;
    return Array.isArray(p) ? p : [];
  }, [userSettings]);

  const poolAlerts = useMemo(
    () => analyzeChampionPool(pool, games, role),
    [pool, games, role]
  );

  // Champions les plus joués sur 30j
  const topChampions = useMemo(() => {
    const cutoff = Date.now() - MONTH_MS;
    const recent = (Array.isArray(games) ? games : [])
      .filter(g => new Date(g.timestamp) > cutoff);
    const counts = {};
    recent.forEach(g => {
      if (!g.champion) return;
      counts[g.champion] = (counts[g.champion] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name]) => name);
  }, [games]);

  return (
    <div className="space-y-6">

      {/* Analyse du pool */}
      <section className="space-y-3">
        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold">
          Analyse du pool
        </div>
        {poolAlerts.length === 0 ? (
          <div className="border border-emerald-400/30 bg-emerald-400/5 px-4 py-3 text-sm text-emerald-300">
            ▲ Ton pool est cohérent. Aucune alerte.
          </div>
        ) : (
          <div className="space-y-2">
            {poolAlerts.map((alert) => {
              const s = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
              const Icon = s.icon;
              return (
                <div key={`${alert.type}-${alert.title}`} className={`border ${s.border} ${s.bg} px-4 py-3`}>
                  <div className="flex items-start gap-3">
                    <Icon size={15} className={`${s.color} shrink-0 mt-0.5`} />
                    <div>
                      <div className={`text-xs font-bold ${s.color} mb-0.5`}>{alert.title}</div>
                      <div className="text-[11px] text-zinc-400 leading-relaxed">{alert.detail}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Coaching par champion */}
      <section className="space-y-3">
        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold">
          Coaching par champion · 30j
        </div>
        {topChampions.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-950 px-4 py-6 text-center text-sm text-zinc-600">
            Pas assez de games récentes pour générer du coaching champion.
          </div>
        ) : (
          <div className="space-y-3">
            {topChampions.map(champ => (
              <ChampionCoachTags
                key={champ}
                champion={champ}
                games={games}
                role={role}
              />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
